import type { DueDiligenceStatus, NotificationPreferences, User } from '@gcv/shared';
import { UserInterests } from '@gcv/shared';
import { inject, injectable } from 'inversify';
import { DateTime } from 'luxon';
import { action, makeAutoObservable } from 'mobx';

import { UsersApi } from 'api';
import { UserType } from 'domain/types';
import { CrbBankStore } from 'stores/CrbBankStore';
import { CrbDispensaryStore } from 'stores/CrbDispensaryStore';
import { UserStore } from 'stores/UserStore';
import { track, updatePendoForCrb } from 'third-party-integrations/pendo';
import { DateTimeHelpers } from 'util/dateTime.util';

export interface PM {
  userInfo: Partial<User>;
  notificationPreferences: NotificationPreferences;
  interests: { [key in UserInterests]: boolean };
}

export interface UserSettings extends PM {}

@injectable()
export class CrbUserSettingsRepo {
  @inject(UserStore) private userStore: UserStore;
  @inject(CrbDispensaryStore) private dispensaryStore: CrbDispensaryStore;
  @inject(CrbBankStore) private crbBankStore: CrbBankStore;

  constructor() {
    makeAutoObservable(this);
  }

  programmersModel: PM = {} as PM;

  updateProgrammersModel = action((programmersModel: Partial<PM>) => {
    this.programmersModel = { ...this.programmersModel, ...programmersModel };
  });

  load = action(async () => {
    const interests = this.userStore.user.interests;
    this.updateProgrammersModel({
      userInfo: {
        firstName: this.userStore.user.firstName,
        minitial: this.userStore.user.minitial,
        lastName: this.userStore.user.lastName,
        email: this.userStore.user.email,
        phone: this.userStore.user.phone,
        address: this.userStore.user.address,
        city: this.userStore.user.city,
        state: this.userStore.user.state,
        zipcode: this.userStore.user.zipcode,
        ssn: this.userStore.user.ssn,
        dateOfBirth: this.userStore.user.dateOfBirth
      },
      notificationPreferences: this.userStore.user.preferences.notifications,
      interests: {
        [UserInterests.AchAndWires]: interests?.includes(UserInterests.AchAndWires) === true,
        [UserInterests.BillPay]: interests?.includes(UserInterests.BillPay) === true,
        [UserInterests.BusinessBanking]: interests?.includes(UserInterests.BusinessBanking) === true,
        [UserInterests.BusinessInsurance]: interests?.includes(UserInterests.BusinessInsurance) === true,
        [UserInterests.CashLogistics]: interests?.includes(UserInterests.CashLogistics) === true,
        [UserInterests.CommercialLoans]: interests?.includes(UserInterests.CommercialLoans) === true,
        [UserInterests.PayrollAndHr]: interests?.includes(UserInterests.PayrollAndHr) === true,
        [UserInterests.Payments]: interests?.includes(UserInterests.Payments) === true
      }
    });
  });

  private userSettingsAreEqual = (a: UserInterests[], b: UserInterests[]): boolean => {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.length !== b.length) return false;
    for (const a_interest of a) {
      if (!b.includes(a_interest)) {
        return false;
      }
    }
    return true;
  };

  public updateUserSettings = async (settings: UserSettings) => {
    const userIdDetails: Partial<User> = {
      firstName: settings.userInfo.firstName,
      minitial: settings.userInfo.minitial,
      lastName: settings.userInfo.lastName,
      email: settings.userInfo.email,
      phone: settings.userInfo.phone,
      address: settings.userInfo.address,
      city: settings.userInfo.city,
      state: settings.userInfo.state,
      zipcode: settings.userInfo.zipcode,
      ssn: settings.userInfo.ssn,
      dateOfBirth: settings.userInfo.dateOfBirth
    };

    const currentDispensary = this.dispensaryStore.currentDispensary;
    const dateOfBirth =
      settings.userInfo.dateOfBirth && DateTime.fromISO(settings.userInfo.dateOfBirth).isValid
        ? DateTimeHelpers.formatISOToDateString(
            settings.userInfo.dateOfBirth,
            currentDispensary.iana_timezone
          )
        : undefined;
    userIdDetails.dateOfBirth = dateOfBirth;
    const usersApi = new UsersApi();
    const interests = Object.keys(settings.interests).filter(
      (interest) => settings.interests[interest as UserInterests]
    ) as UserInterests[];

    const userStoreUser = this.userStore.user;
    let user = await usersApi.updateUserIdentification(userStoreUser.id, userIdDetails);
    const provider = this.dispensaryStore.getDefaultProvider();
    const preferences = await usersApi.updateUserPreferences(userStoreUser.id, {
      notifications: settings.notificationPreferences
    });

    if (!user.interests || !this.userSettingsAreEqual(user.interests, interests)) {
      user = await usersApi.updateUserInterests(userStoreUser.id, interests);
      track('CRB | Marketplace | Change Interests Save', {
        user_id: user.id,
        crb_name: currentDispensary.name,
        [UserInterests.AchAndWires]: interests.includes(UserInterests.AchAndWires),
        [UserInterests.BillPay]: interests.includes(UserInterests.BillPay),
        [UserInterests.BusinessBanking]: interests.includes(UserInterests.BusinessBanking),
        [UserInterests.BusinessInsurance]: interests.includes(UserInterests.BusinessInsurance),
        [UserInterests.CashLogistics]: interests.includes(UserInterests.CashLogistics),
        [UserInterests.CommercialLoans]: interests.includes(UserInterests.CommercialLoans),
        [UserInterests.PayrollAndHr]: interests.includes(UserInterests.PayrollAndHr),
        [UserInterests.Payments]: interests.includes(UserInterests.Payments)
      });
      const activeServiceProviders = this.crbBankStore.getActiveCrbServiceProviders();
      const showCompetitiveMarketPlaceOffers = activeServiceProviders.some(
        (sp) => sp.showCompetitors || sp.activeServiceProvider === false
      );
      updatePendoForCrb(
        currentDispensary,
        user,
        currentDispensary.name,
        provider.id,
        provider.templates.value.onboarding.status as DueDiligenceStatus,
        activeServiceProviders.length,
        activeServiceProviders,
        showCompetitiveMarketPlaceOffers,
        {
          userInterests: interests
        }
      );
    }

    user.preferences = preferences;

    if (user.id === userStoreUser.id) {
      this.userStore.updateUser({ ...user, type: UserType.Dispensary });
    }

    this.dispensaryStore.updateDispensaryStaff(user);

    return this.updateProgrammersModel(settings);
  };
}
